import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { requestsApi, requestTypeSettingsApi, approvalsApi } from '../../lib/api';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, UserPlus, Loader2, PenTool, Pencil, AlertTriangle } from 'lucide-react';
import FtChairmanApprovalModal from './FtChairmanApprovalModal';
import CTAFinalApprovalModal from '../CTAFinalApprovalModal';

/**
 * Renders the next-step action button(s) for a Financial Transaction (FT) request.
 *
 * State machine:
 *   DRAFT (Coordinator) → submit handled by RequestDetails
 *   AWAITING_CHAIRMAN_ASSIGNMENT → Chairman assigns a Financial Officer (a Coordinator)
 *   FT_PENDING_FO_REVIEW → assigned FO (Coordinator) approves
 *   FT_PENDING_CHAIRMAN_FINAL → Chairman approves + configures the e-signature bundle
 *   FT_PENDING_FINANCE → Finance approves
 *   FT_PENDING_DIRECTOR → Director approves
 *   FT_PENDING_EXEC_DIR_REVIEW → Exec Director approves → PENDING_SIGNATURES (cascade)
 */
export function FtWorkflowActions({ request, assignments = [], onChange, checkDocGate, onDiscussionChanged, onStartRoundWithGates }) {
  const { user } = useAuth();
  const [showAssignFo, setShowAssignFo] = useState(false);
  const [foList, setFoList] = useState([]);
  const [selectedFo, setSelectedFo] = useState('');
  const [loadingFos, setLoadingFos] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [showEsignModal, setShowEsignModal] = useState(false);
  const [esignEditMode, setEsignEditMode] = useState(false);
  const [esignRoundMode, setEsignRoundMode] = useState(false);
  // Phase 4: bundle (default for FT) vs per-document e-sign mode — controls which modal renders
  const [esignPerDoc, setEsignPerDoc] = useState(false);
  // Signature rounds availability (Phase 3) — drives the "Start New Signature Round" button
  const [roundsCfg, setRoundsCfg] = useState(null);
  // FO → doc-thread auto-assign confirmation (shown right after assigning the FO)
  const [showFoAutoAssign, setShowFoAutoAssign] = useState(false);
  const [assignedFoName, setAssignedFoName] = useState('');
  const [autoAssigning, setAutoAssigning] = useState(false);
  // Chairman pre-emptive skip of the FO review (mirrors the CTA skip flow)
  const [showSkipFo, setShowSkipFo] = useState(false);
  const [skippingFo, setSkippingFo] = useState(false);

  useEffect(() => {
    let on = true;
    if (request?.type === 'FT' && ['READY_TO_EXECUTE', 'EXECUTED'].includes(request?.status) &&
        (user?.role === 'CHAIRMAN' || user?.role === 'ADMIN')) {
      requestTypeSettingsApi.getSignatureRoundsConfig('FT')
        .then(res => { if (on) setRoundsCfg(res.data); })
        .catch(() => {});
    }
    return () => { on = false; };
  }, [request?.type, request?.status, user?.role]);

  if (!request || request.type !== 'FT') return null;
  const status = request.status;
  const role = user?.role;

  // Determine whether the current user is the next actor
  const isChairmanStage = status === 'AWAITING_CHAIRMAN_ASSIGNMENT' || status === 'FT_PENDING_CHAIRMAN_FINAL';
  const isFoStage = status === 'FT_PENDING_FO_REVIEW';
  const isFinanceStage = status === 'FT_PENDING_FINANCE';
  const isDirectorStage = status === 'FT_PENDING_DIRECTOR';
  const isExecDirStage = status === 'FT_PENDING_EXEC_DIR_REVIEW';

  // The FO is a Coordinator holding a pending FINANCIAL_OFFICER assignment on this FT
  const isAssignedFo = assignments.some(a =>
    a.assignment_role === 'FINANCIAL_OFFICER' && a.assignee_user_id === user?.id && a.status === 'PENDING'
  );

  // Phase 5: stage-configured requests handle review-stage approve/reject via the ReviewStagesPanel
  const hasReviewStages = (request.review_stages?.length || 0) > 0;
  const isReviewStageStatus = isFinanceStage || isDirectorStage || isExecDirStage;

  const canAct =
    (isChairmanStage && (role === 'CHAIRMAN' || role === 'ADMIN')) ||
    (isFoStage && (role === 'ADMIN' || (role === 'COORDINATOR' && isAssignedFo))) ||
    (isFinanceStage && (role === 'FINANCE' || role === 'ADMIN')) ||
    (isDirectorStage && (role === 'DIRECTOR' || role === 'ADMIN')) ||
    (isExecDirStage && (role === 'EXEC_DIRECTOR' || role === 'ADMIN'));

  // Chairman can edit the e-signature config until the FIRST signature is collected
  const canEditEsign = (role === 'CHAIRMAN' || role === 'ADMIN') &&
    ['FT_PENDING_FINANCE', 'FT_PENDING_DIRECTOR', 'FT_PENDING_EXEC_DIR_REVIEW', 'PENDING_SIGNATURES'].includes(status) &&
    request.type === 'FT';

  // Phase 3: Chairman can start an additional signature round once everything is signed.
  // iter167 Issue 29 — Parity with CTA (iter148f): hide once the request is EXECUTED.
  // After final execution no further rounds make sense; an admin who needs to re-open
  // uses the "Reopen request" undo banner instead. Previously the button also showed
  // on EXECUTED FT requests, which confused the chairman post-execute.
  const canStartRound = (role === 'CHAIRMAN' || role === 'ADMIN') &&
    status === 'READY_TO_EXECUTE' &&
    !!roundsCfg?.allow_additional_rounds;

  // Chairman keeps the main green approve button during FO review — clicking it offers to
  // skip the pending FO approval (CTA-style) and jump straight to e-signature configuration
  const canChairmanSkipFo = isFoStage && (role === 'CHAIRMAN' || role === 'ADMIN' || role === 'CRD');
  const pendingFos = assignments.filter(a => a.assignment_role === 'FINANCIAL_OFFICER' && a.status === 'PENDING');

  const canReject = canAct && !(hasReviewStages && isReviewStageStatus);

  const openAssignFo = async () => {
    setLoadingFos(true);
    try {
      const res = await requestsApi.ftEligibleFos(request.id);
      setFoList(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load eligible Coordinators');
    } finally {
      setLoadingFos(false);
    }
    setShowAssignFo(true);
  };

  const handleAssignFo = async () => {
    if (!selectedFo) {
      toast.error('Please select a Financial Officer');
      return;
    }
    setBusy(true);
    try {
      await requestsApi.ftAssignFo(request.id, selectedFo);
      const foName = foList.find(f => f.id === selectedFo)?.name || 'The Financial Officer';
      toast.success('Financial Officer assigned');
      setShowAssignFo(false);
      setSelectedFo('');
      // Ask the Chairman about auto-assigning the FO to document threads
      setAssignedFoName(foName);
      setShowFoAutoAssign(true);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to assign Financial Officer');
    } finally {
      setBusy(false);
    }
  };

  const handleFoAutoAssign = async () => {
    setAutoAssigning(true);
    try {
      await requestsApi.ftFoThreadAutoassign(request.id);
      toast.success(`${assignedFoName} auto-assigned to all document threads (existing & future)`);
      setShowFoAutoAssign(false);
      onChange?.();
      onDiscussionChanged?.(); // show the green "Assigned to" markers in the Discussion tab immediately
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-assignment failed');
    } finally {
      setAutoAssigning(false);
    }
  };

  const handleApprove = async () => {
    // Doc-thread gate: the approver must first approve every doc thread assigned to them
    if (await checkDocGate?.()) return;
    setBusy(true);
    try {
      if (isFoStage) await requestsApi.ftFoApprove(request.id);
      else if (isFinanceStage) await requestsApi.ftFinanceApprove(request.id);
      else if (isDirectorStage) await requestsApi.ftDirectorApprove(request.id);
      else if (isExecDirStage) await requestsApi.ftExecdirApprove(request.id);
      toast.success(isExecDirStage ? 'Approved — signature phase started' : 'Approval recorded');
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    } finally {
      setBusy(false);
    }
  };

  const handleChairmanApproveClick = async () => {
    // Doc-thread gate applies to the Chairman's final approval too.
    // Use scope='all' so the gate fires for ANY non-APPROVED doc thread on the request,
    // not only those assigned to the Chairman personally.
    if (await checkDocGate?.('all')) return;
    setEsignEditMode(false);
    setEsignRoundMode(false);
    // iter163d — FT "Approve & Configure E-Signature" now opens the CTA per-document
    // modal (with the Review Stages wizard) just like CTA's "Start Review & E-Signature
    // Process". Bundle mode is fully retired for new approvals.
    setEsignPerDoc(true);
    setShowEsignModal(true);
  };

  // Chairman clicks the green approve button while the FO review is still pending
  const handleChairmanSkipFoClick = async () => {
    if (await checkDocGate?.('all')) return;
    setShowSkipFo(true);
  };

  const handleConfirmSkipFo = async () => {
    setSkippingFo(true);
    try {
      const res = await requestsApi.ftSkipFo(request.id);
      const n = res.data?.skipped_count ?? 0;
      toast.success(n > 0
        ? `Skipped ${n} pending approval${n === 1 ? '' : 's'}. Configure e-signature to proceed.`
        : 'Skipped Financial Officer review. Configure e-signature to proceed.');
      setShowSkipFo(false);
      onChange?.();
      // Re-check doc-thread approval state for the WHOLE request before opening e-sign config.
      // If any thread is still PENDING_REVIEW / CHANGES_REQUESTED, the gate dialog opens instead.
      if (await checkDocGate?.('all')) return;
      // Jump straight into Final Approval & E-Signature Configuration
      setEsignEditMode(false);
      setEsignRoundMode(false);
      // iter163d — Skip-FO path also opens the CTA per-document modal directly.
      setEsignPerDoc(true);
      setShowEsignModal(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to skip the Financial Officer review');
    } finally {
      setSkippingFo(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setBusy(true);
    try {
      await requestsApi.ftReject(request.id, rejectReason.trim());
      toast.success('Financial Transaction rejected');
      setShowReject(false);
      setRejectReason('');
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusy(false);
    }
  };

  // Keep the component mounted while the FO auto-assign popup is open — after assigning,
  // the request moves to FT_PENDING_FO_REVIEW where the Chairman has no other actions,
  // which would otherwise unmount (and instantly close) the dialog.
  if (!canAct && !canEditEsign && !showFoAutoAssign && !canChairmanSkipFo && !canStartRound) return null;

  return (
    <>
      {canAct && status === 'AWAITING_CHAIRMAN_ASSIGNMENT' && (
        <Button
          onClick={openAssignFo}
          className="bg-emerald-600 hover:bg-emerald-700"
          disabled={busy}
          data-testid="ft-assign-fo-btn"
        >
          <UserPlus size={16} className="mr-2" />
          Assign Financial Officer
        </Button>
      )}
      {canAct && status === 'FT_PENDING_CHAIRMAN_FINAL' && (
        <Button
          onClick={handleChairmanApproveClick}
          className="bg-emerald-600 hover:bg-emerald-700"
          disabled={busy}
          data-testid="ft-chairman-approve-btn"
        >
          <PenTool size={16} className="mr-2" />
          Start Review & E-Signature Process
        </Button>
      )}
      {canChairmanSkipFo && (
        <Button
          onClick={handleChairmanSkipFoClick}
          className="bg-emerald-600 hover:bg-emerald-700"
          disabled={busy || skippingFo}
          data-testid="ft-chairman-skip-fo-btn"
        >
          <PenTool size={16} className="mr-2" />
          Start Review & E-Signature Process
        </Button>
      )}
      {canAct && (isFoStage || (isReviewStageStatus && !hasReviewStages)) && (
        <Button
          onClick={handleApprove}
          className="bg-emerald-600 hover:bg-emerald-700"
          disabled={busy}
          data-testid="ft-approve-btn"
        >
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
          {isExecDirStage ? 'Approve & Start E-Signature' : 'Approve'}
        </Button>
      )}
      {canEditEsign && (
        <Button
          variant="outline"
          onClick={() => {
            setEsignEditMode(true);
            setEsignRoundMode(false);
            // iter163d — Editing a legacy bundle-configured FT (pre-bundle-mode-removal) keeps
            // the bundle modal so the in-flight bundle config can still be tweaked. Every
            // other FT (new-style per-doc, or per-doc-from-creation) edits in the CTA-style
            // per-document modal. New first approvals / new rounds / skip-FO never go bundle.
            setEsignPerDoc(!request.ft_bundle_document_id);
            setShowEsignModal(true);
          }}
          disabled={busy}
          data-testid="ft-edit-esign-btn"
        >
          <Pencil size={16} className="mr-2" />
          Edit E-Signature
        </Button>
      )}
      {canStartRound && (
        <Button
          variant="outline"
          onClick={async () => {
            // iter167 Issue 27 — FT parity with CTA: respect both gates
            // before opening the New Signature Round picker.
            //   1) Doc-thread approval gate — every unapproved thread (single
            //      or bundle) must be approved first, OR the chairman explicitly
            //      bypasses via the doc-gate dialog.
            //   2) Pending-approvals gate — any users on this request who
            //      haven't approved yet trigger the Skip-Approvals dialog so
            //      the chairman either nudges them or skips intentionally.
            // The parent RequestDetails owns the dialog state (mirrors how
            // CTA's `openStartRoundWithGates` works) so we delegate via the
            // `onStartRoundWithGates` callback. Fallback path opens the modal
            // directly if the callback is missing — preserves pre-fix behaviour
            // for tests / non-standard mount points.
            if (typeof onStartRoundWithGates === 'function') {
              setEsignEditMode(false);
              setEsignRoundMode(true);
              setEsignPerDoc(true);
              await onStartRoundWithGates(() => setShowEsignModal(true));
              return;
            }
            setEsignEditMode(false);
            setEsignRoundMode(true);
            setEsignPerDoc(true);
            setShowEsignModal(true);
          }}
          className="border-blue-500 text-blue-700 hover:bg-blue-50"
          disabled={busy}
          data-testid="ft-start-signature-round-btn"
        >
          <PenTool size={16} className="mr-2" />
          Start New Signature Round
        </Button>
      )}
      {canReject && (
        <Button
          variant="outline"
          onClick={() => setShowReject(true)}
          className="border-red-300 text-red-700 hover:bg-red-50"
          disabled={busy}
          data-testid="ft-reject-btn"
        >
          <XCircle size={16} className="mr-2" />
          Reject
        </Button>
      )}

      {/* iter163d — Legacy bundle modal stays mounted ONLY so existing bundle-configured
          FTs can still edit their in-flight bundle config; the "Switch to per-document
          mode" link has been removed. All new first approvals, skip-FO flows, new
          signature rounds, and per-doc edits go to CTAFinalApprovalModal directly,
          mirroring the CTA "Start Review & E-Signature Process" experience. */}
      <FtChairmanApprovalModal
        open={showEsignModal && !esignPerDoc}
        onOpenChange={setShowEsignModal}
        request={request}
        editMode={esignEditMode}
        roundMode={esignRoundMode}
        onSuccess={() => onChange?.()}
      />
      <CTAFinalApprovalModal
        open={showEsignModal && esignPerDoc}
        onOpenChange={setShowEsignModal}
        requestId={request.id}
        request={request}
        requestType="FT"
        editMode={esignEditMode}
        roundMode={esignRoundMode}
        onSuccess={() => onChange?.()}
      />

      {/* Skip Financial Officer review — Chairman pre-emptive approval (mirrors CTA skip flow) */}
      <AlertDialog open={showSkipFo} onOpenChange={(open) => { if (!skippingFo) setShowSkipFo(open); }}>
        <AlertDialogContent data-testid="ft-skip-fo-dialog" className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              {pendingFos.length === 0
                ? 'Approve without Financial Officer review?'
                : pendingFos.length === 1
                  ? 'Skip Financial Officer approval?'
                  : `Skip ${pendingFos.length} pending approvals?`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                {pendingFos.length === 0 ? (
                  <p>
                    The Financial Officer has not completed their review yet. Approving now will bypass the
                    Financial Officer review stage and take you directly to{' '}
                    <strong>Final Approval &amp; E-Signature Configuration</strong>.
                  </p>
                ) : (
                  <>
                    <p>The following team member{pendingFos.length === 1 ? ' has' : 's have'} not yet approved this request:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      {pendingFos.map((p) => (
                        <li key={p.id} data-testid={`ft-pending-fo-${p.id}`}>
                          <strong>Financial Officer</strong>
                          {' — '}
                          {p.assignee_name}
                        </li>
                      ))}
                    </ul>
                    <p>
                      Approving now will skip{' '}
                      {pendingFos.length === 1 ? 'this review' : 'ALL of these reviews'} and take you directly to{' '}
                      <strong>Final Approval &amp; E-Signature Configuration</strong>. Skipped assignments will be
                      marked in the audit log and shown on the request page.
                    </p>
                  </>
                )}
                <p className="text-xs text-slate-500">This action is logged in the audit trail.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ft-cancel-skip-fo-btn" disabled={skippingFo}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirmSkipFo(); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ft-confirm-skip-fo-btn"
              disabled={skippingFo}
            >
              {skippingFo ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>
              ) : pendingFos.length === 0 ? (
                <>Approve &amp; Continue →</>
              ) : (
                <>Skip {pendingFos.length} Approver{pendingFos.length === 1 ? '' : 's'} &amp; Continue →</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Financial Officer modal */}
      <Dialog open={showAssignFo} onOpenChange={setShowAssignFo}>
        <DialogContent data-testid="ft-assign-fo-modal">
          <DialogHeader>
            <DialogTitle>Assign Financial Officer</DialogTitle>
          </DialogHeader>
          {loadingFos ? (
            <div className="py-4 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></div>
          ) : foList.length === 0 ? (
            <p className="py-3 text-sm text-slate-600">No eligible Coordinators available. The request creator and Coordinators already assigned to this request are excluded.</p>
          ) : (
            <div className="space-y-3 py-2">
              <Label>Financial Officer *</Label>
              <p className="text-xs text-slate-500">The Financial Officer is a Coordinator who works alongside the request creator on this Financial Transaction.</p>
              <Select value={selectedFo} onValueChange={setSelectedFo}>
                <SelectTrigger data-testid="ft-fo-select"><SelectValue placeholder="Select a Financial Officer..." /></SelectTrigger>
                <SelectContent>
                  {foList.map(fo => (
                    <SelectItem key={fo.id} value={fo.id}>{fo.name} ({fo.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignFo(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleAssignFo} disabled={busy || !selectedFo} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ft-assign-fo-confirm-btn">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FO → document threads auto-assign confirmation */}
      <Dialog open={showFoAutoAssign} onOpenChange={setShowFoAutoAssign}>
        <DialogContent data-testid="ft-fo-autoassign-modal">
          <DialogHeader>
            <DialogTitle>Auto-assign to document threads?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 py-1">
            <span className="font-semibold">{assignedFoName}</span> has been assigned as Financial Officer.
            Do you also want to auto-assign them to <span className="font-semibold">every document thread</span> on
            this request — both the threads that exist right now and any future document uploads?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFoAutoAssign(false)} disabled={autoAssigning} data-testid="ft-fo-autoassign-skip-btn">
              No, skip
            </Button>
            <Button onClick={handleFoAutoAssign} disabled={autoAssigning} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ft-fo-autoassign-confirm-btn">
              {autoAssigning ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Yes, auto-assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject modal */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent data-testid="ft-reject-modal">
          <DialogHeader>
            <DialogTitle>Reject Financial Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Reason *</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this Financial Transaction is being rejected..."
              rows={4}
              data-testid="ft-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleReject} disabled={busy || !rejectReason.trim()} className="bg-red-600 hover:bg-red-700" data-testid="ft-reject-confirm-btn">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FtWorkflowActions;
